import { useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, arrayUnion, Timestamp } from 'firebase/firestore';
import { updateUserProfile } from '../services/firestore';
import { PRICE_IDS, checkSubscriptionTier } from '../services/stripe';

/**
 * Invisible component that keeps User Profile in sync with Stripe Subscriptions.
 * Requires "Run Payments with Stripe" extension to be writing to 'customers' collection.
 */
export default function SubscriptionSync({ user }) {
    useEffect(() => {
        if (!user?.uid) return;
        if (user.isBanned) return; // Banned users cannot sync (preserves 'global' state set by ban)

        // Listen to ALL subscriptions for better debugging and robustness
        const q = query(
            collection(db, 'customers', user.uid, 'subscriptions')
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            let superFanExpiry = null;
            let supporterExpiry = null;

            if (snapshot.empty) {
                console.log("[SubscriptionSync] No subscriptions found at all.");
            }

            snapshot.docs.forEach(doc => {
                const sub = doc.data();
                console.log(`[SubscriptionSync] Sub ${doc.id} Status: ${sub.status}, Price: ...`);

                // Filter active status
                if (sub.status !== 'active' && sub.status !== 'trialing') {
                    return;
                }

                // Robust extraction
                const items = sub.items?.data || sub.items || [];
                const firstItem = items[0];
                const priceId = firstItem?.price?.id || sub.plan?.id || sub.price?.id;

                console.log(`[SubscriptionSync] Active Sub PriceID: ${priceId}`);

                let expiryDate = null;
                const rawEnd = sub.current_period_end;

                if (rawEnd) {
                    if (rawEnd.seconds) {
                        // Firestore Timestamp
                        expiryDate = new Date(rawEnd.seconds * 1000);
                    } else if (typeof rawEnd === 'number') {
                        // Stripe Seconds (standard) - Ensure we don't double-multiply if it looks huge
                        if (rawEnd > 1e12) {
                            // Already milliseconds? (Safety catch)
                            expiryDate = new Date(rawEnd);
                        } else {
                            expiryDate = new Date(rawEnd * 1000);
                        }
                    } else if (typeof rawEnd.toDate === 'function') {
                        try { expiryDate = rawEnd.toDate(); } catch (e) { }
                    }
                }

                console.log(`[SubscriptionSync] Debug - ID: ${priceId}, RawEnd: ${JSON.stringify(rawEnd)}, Parsed: ${expiryDate}, MappedTier: ${Number(checkSubscriptionTier(priceId) !== null)}`);

                if (expiryDate) {
                    const ts = Timestamp.fromDate(expiryDate);

                    let tier = checkSubscriptionTier(priceId); // Primary check (ID)

                    // FALLBACK: Match by Amount (Handle Test Mode vs Live Mismatch)
                    if (!tier) {
                        const amount = firstItem?.price?.unit_amount || sub.plan?.amount || sub.price?.unit_amount;
                        if (amount === 500) tier = 'supporter'; // $5.00
                        if (amount === 1000) tier = 'super_fan'; // $10.00

                        if (tier) console.log(`[SubscriptionSync] Matched Tier by Amount (${amount}): ${tier}`);
                    }

                    if (tier === 'super_fan') {
                        superFanExpiry = ts;
                    } else if (tier === 'supporter') {
                        supporterExpiry = ts;
                    }
                }
            });

            const hasSuperFan = !!superFanExpiry;
            const hasSupporter = !!supporterExpiry;

            let finalTier = 'global';
            if (hasSuperFan && hasSupporter) finalTier = 'mega_fan';
            else if (hasSuperFan) finalTier = 'super_subscriber';
            else if (hasSupporter) finalTier = 'subscriber';

            console.log(`[SubscriptionSync] Calculated Tier: ${finalTier}, Current: ${user.subscriptionTier}`);

            const updates = {};
            let needsUpdate = false;

            // 1. Sync Tier (Upgrade OR Downgrade)
            // Fix: If User is 'admin'/'super_admin' (string), we SHOULD overwrite it to 'subscriber' 
            // if they paid, because permissions are handled by isAdmin/isSuperAdmin BOOLEANS.
            // This ensures they get the correct badge.

            // We only protect 'partner' or 'moderator' if those imply specific badges not compatible with sub.
            // But for Admin, we want them to see their Sub status.

            const isProtectedRole = ['partner', 'moderator'].includes(user.subscriptionTier);

            // Only protect against DOWNGRADES for special roles. 
            // If we are upgrading to a paid tier, we always allow it (even for admins).
            const isUpgrade = finalTier !== 'global';

            const isProtected = isProtectedRole && !isUpgrade;

            if (!isProtected && user.subscriptionTier !== finalTier) {
                if (finalTier === 'global' && user.subscriptionStatus === 'gifted') {
                    console.log("[SubscriptionSync] User is gifted, ignoring Stripe empty state.");
                } else {
                    updates.subscriptionTier = finalTier;
                    if (finalTier === 'global') {
                        updates.subscriptionStatus = 'canceled';
                    } else {
                        updates.subscriptionStatus = 'active';
                    }
                    needsUpdate = true;
                }
            } else if (user.subscriptionTier !== finalTier && !isProtected) {
                // Determine upgrade - Redundant block but kept for structural clarity if merged above
                updates.subscriptionTier = finalTier;
                updates.subscriptionStatus = 'active';
                needsUpdate = true;
            }

            // 2. Sync Expiry Dates & Calculate Final Tier
            // Logic: Effectively Merge Stripe Data with existing Firestore Data (Gifts)
            // We use the LATEST date for each tier.

            let finalSuperEnd = superFanExpiry;
            let finalSupporterEnd = supporterExpiry;

            // Check existing user data (Gifts/One-Time payments)
            if (user.superSubscriberEnd) {
                const existingSuper = user.superSubscriberEnd.seconds ? new Date(user.superSubscriberEnd.seconds * 1000) : new Date(user.superSubscriberEnd);
                if (!finalSuperEnd || existingSuper > finalSuperEnd.toDate()) {
                    finalSuperEnd = Timestamp.fromDate(existingSuper);
                }
            }

            if (user.subscriberEnd) {
                const existingSub = user.subscriberEnd.seconds ? new Date(user.subscriberEnd.seconds * 1000) : new Date(user.subscriberEnd);
                if (!finalSupporterEnd || existingSub > finalSupporterEnd.toDate()) {
                    finalSupporterEnd = Timestamp.fromDate(existingSub);
                }
            }

            // Determine Combined Tier based on Effective Dates
            const now = new Date();
            const isSuperActive = finalSuperEnd && finalSuperEnd.toDate() > now;
            const isSupporterActive = finalSupporterEnd && finalSupporterEnd.toDate() > now;

            if (isSuperActive && isSupporterActive) finalTier = 'mega_fan';
            else if (isSuperActive) finalTier = 'super_subscriber';
            else if (isSupporterActive) finalTier = 'subscriber';


            if (finalSuperEnd) {
                // Only update if significantly different ( > 1 min) to prevent loops
                const current = user.superSubscriberEnd ? (user.superSubscriberEnd.seconds * 1000) : 0;
                if (Math.abs(finalSuperEnd.toMillis() - current) > 60000) {
                    updates.superSubscriberEnd = finalSuperEnd;
                    needsUpdate = true;
                }
            }
            if (finalSupporterEnd) {
                const current = user.subscriberEnd ? (user.subscriberEnd.seconds * 1000) : 0;
                if (Math.abs(finalSupporterEnd.toMillis() - current) > 60000) {
                    updates.subscriberEnd = finalSupporterEnd;
                    needsUpdate = true;
                }
            }

            // Set main subscriptionEnd (max of both)
            const maxExpiry = (finalSuperEnd && finalSupporterEnd)
                ? (finalSuperEnd.toMillis() > finalSupporterEnd.toMillis() ? finalSuperEnd : finalSupporterEnd)
                : (finalSuperEnd || finalSupporterEnd);

            if (maxExpiry) {
                const current = user.subscriptionEnd ? (user.subscriptionEnd.seconds * 1000) : 0;
                if (Math.abs(maxExpiry.toMillis() - current) > 60000) {
                    updates.subscriptionEnd = maxExpiry;
                    needsUpdate = true;
                }
            }

            // 3. Unlock Hidden Badge (Mega Fan)
            if (finalTier === 'mega_fan') {
                const hasGoldCrown = user.badges?.some(b => b.id === 'vip_gold_crown' && b.unlocked);
                if (!hasGoldCrown) {
                    const now = new Date();
                    updates.badges = arrayUnion({
                        id: 'vip_gold_crown',
                        unlocked: true,
                        unlockedAt: now.toISOString(),
                        name: 'Hidden Gold Crown',
                        color: '#FFD700'
                    });
                    needsUpdate = true;
                }
            }

            if (needsUpdate) {
                console.log(`[SubscriptionSync] Applying updates:`, updates);
                try {
                    // Race against a 5s timeout to detect hangs
                    const updatePromise = updateUserProfile(user.uid, updates);
                    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("TIMEOUT_UPDATING_PROFILE")), 5000));

                    await Promise.race([updatePromise, timeoutPromise]);

                    console.log("[SubscriptionSync] SUCCESS");
                    if (finalTier !== 'global') {
                        // alert("DEBUG: Sync Success! You should be " + finalTier);
                    }
                } catch (e) {
                    console.error("Error syncing subscription profile:", e);
                    // alert("DEBUG: Sync Failed! " + e.message);
                }
            }
        });

        return () => unsubscribe();
    }, [user?.uid, user?.subscriptionTier, user?.isBanned, user?.superSubscriberEnd, user?.subscriberEnd]); // Fix: Re-run if dates change (Stale Closure)

    return null;
}
